import React from 'react';
import { Container, Box, Typography, Button, useTheme, useMediaQuery } from '@mui/material';

interface Product {
  id: number;
  title: string;
  image: string;
  description: string;
  affiliateLink: string;
}

// Sample product data - replace with your actual products
const products: Product[] = [
  {
    id: 1,
    title: "Silicone Steamer",
    image: "https://i.pinimg.com/736x/64/bf/2d/64bf2d92f9ed4bb3bd3e9e4f02ceaa99.jpg",
    description: "This compact silicone steam case is ideal for cooking healthy meals for 1 to 2 people. It preserves nutrients by steaming food at the perfect temperature and includes a tray for stew-like recipes. Microwave and dishwasher safe for easy use and cleaning.",
    affiliateLink: "https://amzn.to/3Gf01he"
  },
  // Add more products here
];

const ProductGrid: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.between('sm', 'md'));

  return (
    <Box 
      component="main"
      sx={{ 
        backgroundColor: '#ffffff',
        minHeight: '100vh',
        width: '100%',
        py: { xs: 4, sm: 6, md: 8 },
        px: { xs: 2, sm: 3, md: 4 },
      }}
    >
      <Container 
        maxWidth="lg"
        sx={{
          px: { xs: 2, sm: 3, md: 4 },
        }}
      >
        <Box
          component="header"
          sx={{
            mb: { xs: 6, sm: 8, md: 10 },
            maxWidth: '800px',
            mx: 'auto',
          }}
        >
          <Typography
            variant="h1"
            component="h1"
            sx={{
              fontSize: { xs: '1.75rem', sm: '2rem', md: '2.5rem' },
              fontWeight: 800,
              textAlign: 'center',
              mb: { xs: 2, sm: 3 },
              color: '#2C3E50',
              letterSpacing: '-0.02em',
            }}
          >
            The Nutri Shelf
          </Typography>
          <Typography
            variant="h2"
            component="p"
            sx={{
              fontSize: { xs: '1rem', sm: '1.1rem', md: '1.25rem' },
              fontWeight: 400,
              textAlign: 'center',
              color: '#64748b',
              maxWidth: '600px',
              mx: 'auto',
              lineHeight: { xs: 1.5, md: 1.6 },
              px: { xs: 2, sm: 0 },
            }}
          >
            Nutrition made easy and accessible. Discover our curated selection of products designed to help you build sustainable healthy habits.
          </Typography>
        </Box>

        <Box 
          component="section"
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: { xs: 3, sm: 4, md: 5 },
            maxWidth: '1200px',
            mx: 'auto',
          }}
        >
          {products.map((product) => (
            <Button
              key={product.id}
              href={product.affiliateLink}
              target="_blank"
              rel="noopener noreferrer"
              aria-label={`Shop ${product.title} on Amazon`}
              sx={{
                width: '100%',
                backgroundColor: '#ffffff',
                color: '#2C3E50',
                textTransform: 'none',
                padding: 0,
                borderRadius: { xs: '8px', sm: '12px' },
                overflow: 'hidden',
                boxShadow: '0 2px 20px rgba(0, 0, 0, 0.06)',
                transition: 'all 0.3s ease-in-out',
                '&:hover': {
                  backgroundColor: '#ffffff',
                  transform: 'translateY(-4px)',
                  boxShadow: '0 4px 25px rgba(0, 0, 0, 0.1)',
                  '& .product-image': {
                    transform: 'scale(1.03)',
                  },
                  '& .cta-text': {
                    color: '#0D6B4C',
                    '& .arrow': {
                      transform: 'translateX(4px)',
                    }
                  }
                },
                '@media (hover: none)': {
                  '&:hover': {
                    transform: 'none',
                    '& .product-image': {
                      transform: 'none',
                    },
                  },
                },
              }}
            >
              <Box sx={{ 
                display: 'flex',
                flexDirection: { xs: 'column', md: 'row' },
                alignItems: 'stretch',
                width: '100%',
                textAlign: 'left',
                minHeight: { md: '400px' },
              }}>
                <Box sx={{ 
                  width: { xs: '100%', md: '45%' },
                  height: { xs: '250px', sm: '300px', md: 'auto' },
                  position: 'relative',
                  overflow: 'hidden',
                  backgroundColor: '#f8f9fa',
                }}>
                  <Box
                    component="img"
                    src={product.image}
                    alt={product.title}
                    loading="lazy"
                    className="product-image"
                    sx={{ 
                      height: '100%',
                      width: '100%',
                      objectFit: 'cover',
                      transition: 'transform 0.6s cubic-bezier(0.4, 0, 0.2, 1)',
                    }}
                  />
                </Box>
                <Box sx={{ 
                  p: { xs: 3, sm: 4, md: 5 },
                  flex: 1,
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                }}>
                  <Typography 
                    variant="h6"
                    component="h2"
                    sx={{ 
                      fontWeight: 700,
                      fontSize: { xs: '1.25rem', sm: '1.5rem', md: '1.75rem' },
                      mb: { xs: 1.5, sm: 2 },
                      color: '#2C3E50',
                      letterSpacing: '-0.01em',
                      lineHeight: 1.3,
                    }}
                  >
                    {product.title}
                  </Typography>
                  <Typography 
                    variant="body1"
                    component="p"
                    sx={{ 
                      color: '#64748b',
                      fontSize: { xs: '1rem', sm: '1.1rem' },
                      lineHeight: { xs: 1.6, md: 1.7 },
                      mb: { xs: 3, sm: 4 },
                      maxWidth: '600px',
                    }}
                  >
                    {product.description}
                  </Typography>
                  <Box
                    className="cta-text"
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      color: '#2C3E50',
                      fontWeight: 600,
                      fontSize: { xs: '1rem', sm: '1.1rem' },
                      transition: 'color 0.3s ease',
                      mt: 'auto',
                    }}
                  >
                    Shop Now
                    <span 
                      className="arrow" 
                      aria-hidden="true"
                      style={{
                        marginLeft: '8px',
                        transition: 'transform 0.3s ease',
                        display: 'inline-block',
                      }}
                    >
                      →
                    </span>
                  </Box>
                </Box>
              </Box>
            </Button>
          ))}
        </Box>
      </Container>
    </Box>
  );
};

export default ProductGrid; 